import { useApolloClient } from '@apollo/client';
import React, { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  NotificationsDocument,
  NotificationsQuery,
  useClickNotificationMutation,
  useNotificationsQuery,
  useReadNotificationsMutation,
} from '../types/api.graphql';
import { NotificationSubscriptionGQL } from '../types/subscriptions/notifications';
import NotificationHandler from '../utils/notifications/NotificationHandler';

type NotificationsNode = NotificationsQuery['notifications']['nodes'][number];

export const NotificationContext = React.createContext<NotificationProviderValuesTypes>(undefined as any);

export const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationProviderProps = {
  children: ReactNode;
};

interface NotificationProviderValuesTypes {
  notifications: () => NotificationsQuery | null;
  unreadCount: number;
  lastNotificationDate: Date;
  markAsRead: (id: string) => void;
  resetUnreadCount: () => void;
  loadingNotifications?: boolean;
  loadMoreNotifications?: () => void;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [unreadCount, setUnreadCount] = useState(undefined);
  const { cache } = useApolloClient();
  const unsubscribeRef = React.useRef<any>(undefined);
  const navigate = useNavigate();

  const {
    data: notificationsData,
    loading: loadingNotifications,
    fetchMore: fetchMoreNotifications,
    subscribeToMore,
  } = useNotificationsQuery({
    onCompleted(data: NotificationsQuery) {
      setUnreadCount(data.notifications.unreadNotifications);
    },
    variables: { first: NOTIFICATIONS_PAGE_SIZE },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const [clickNotificationMutation] = useClickNotificationMutation();
  const [readNotificationsMutation] = useReadNotificationsMutation();

  const readCache = (): NotificationsQuery => {
    return cache.readQuery({ query: NotificationsDocument });
  };

  const notifications = () => {
    return readCache() || notificationsData;
  };

  const markAsRead = (id: string) => {
    void (async () => {
      await clickNotificationMutation({
        variables: { clickNotificationId: id },
        update(cache: any) {
          const data = readCache();
          if (data?.notifications) {
            const newNotifications =
              data.notifications.nodes?.map((n: NotificationsNode) => {
                if (n.id !== id) return n;
                return { ...n, clicked: true };
              }) || [];
            cache.writeQuery({
              query: NotificationsDocument,
              data: {
                notifications: {
                  ...data.notifications,
                  nodes: newNotifications,
                  unreadNotifications: data.notifications.unreadNotifications - 1,
                },
              },
            });
          }
        },
      });
    })();
  };

  const resetUnreadCount = () => {
    void (async () => {
      await readNotificationsMutation({
        update(cache: any) {
          const data = readCache();
          if (data?.notifications) {
            cache.writeQuery({
              query: NotificationsDocument,
              data: {
                notifications: {
                  ...data.notifications,
                  unreadNotifications: 0,
                },
              },
            });
          }
        },
      });
    })();
  };

  const loadMoreNotifications = () => {
    void (async () => {
      await fetchMoreNotifications?.({
        variables: {
          after: notificationsData?.notifications?.pageInfo?.endCursor,
        },
      });
    })();
  };

  const getLastNotificationDate = () => {
    const lastcreatedAt = notifications()?.notifications?.nodes?.[0]?.createdAt;
    return lastcreatedAt ? new Date(lastcreatedAt) : undefined;
  };

  const lastNotificationDate = getLastNotificationDate();

  React.useEffect(() => {
    if (!subscribeToMore) return null;
    unsubscribeRef.current?.();
    unsubscribeRef.current = subscribeToMore({
      document: NotificationSubscriptionGQL,
      updateQuery: (prev, { subscriptionData }: any) => {
        const notificationData = subscriptionData?.data?.notificationCreated;
        if (!notificationData) return prev;

        const unreadNotifications = prev.notifications.unreadNotifications + 1;
        const totalCount = prev.notifications.totalCount + 1;
        const handler = NotificationHandler.createHandler(notificationData);
        handler.showToast({ markAsRead, navigate });
        return Object.assign({}, prev, {
          notifications: {
            ...prev.notifications,
            nodes: [notificationData, ...(prev.notifications.nodes || [])],
            unreadNotifications: unreadNotifications,
            totalCount: totalCount,
          },
        });
      },
      onError: () => {
        return;
      },
    });
    return () => {
      unsubscribeRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToMore]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loadingNotifications,
        loadMoreNotifications,
        unreadCount,
        lastNotificationDate,
        markAsRead,
        resetUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotificationContext() {
  return React.useContext(NotificationContext);
}
